import ContentBody from "../common/content-body/ContentBody";
import {Button, Grid, IconButton, Paper, useTheme} from "@mui/material";
import {FormProvider, useForm} from "react-hook-form";
import {DownloadRounded, ReplayRounded, SearchRounded} from "@mui/icons-material";
import AgGridCustomTable from "../../../util/component/AgGridCustomTable";
import {ColDef, ICellRendererParams, IServerSideDatasource} from "ag-grid-community";
import CustomLoader from "../../../util/component/CustomLoader";
import React, {useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import {getFormDirtyValues} from "../utils";
import {FormAutocomplete, FormDatePicker, FormTextField} from "../common/form-fields-wrappers";
import {searchDocuments} from "./documentalHistory.actions";
import {APPLICATION_DOCUMENTS_FORM_VALIDATION_SCHEMA} from "../application-detail/config";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs, {Dayjs} from "dayjs";

interface IDocumentHistorySearch {
    from: Dayjs | null,
    to: Dayjs | null,
    documentName: string | null,
    fileName: string | null,
    note: string | null
}

const DocumentalHistory = (props: any) => {
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);
    const [ isSearching, setIsSearching ] = useState(false);

    const searchDocumentsForm = useForm<IDocumentHistorySearch>({
        defaultValues: {
            from: null,
            to: null,
            documentName: null,
            fileName: "",
            note: ""
        },
        resolver: yupResolver(APPLICATION_DOCUMENTS_FORM_VALIDATION_SCHEMA, {}, {rawValues: true})
    });

    const gridRef = useRef<AgGridReact>(null);
    const [isFetching, setIsFetcing] = useState<boolean>(false);

    const downloadFunc = (data: string, fileName?: string) => {
        const linkSource = data;
        const downloadLink = document.createElement('a');
        downloadLink.href = linkSource;
        downloadLink.download = fileName ? fileName : 'file';
        downloadLink.click();
    };
    const downloadDocument = (base64: string | ArrayBuffer, fileName?: string) => {
        downloadFunc(base64.toString(), fileName);
    };


    const columnDefs = useMemo<ColDef[]>(() => [
        {field: 'documentName', headerName: t('documentalDetails.table.title')},
        {field: 'uploadDate', headerName: t('documentalDetails.table.uploadDate'), valueFormatter: function (params) {
                return dayjs(params.value).format('DD/MM/YYYY');
            },},
        {field: 'fileName', headerName: t('documentalDetails.table.fileName'), minWidth: 300},
        {field: 'version', headerName: t('documentalDetails.table.version')},
        {field: 'note', headerName: t('documentalDetails.table.note')},
        {
            field: "actions",
            headerName: t('documentalDetails.table.actions'),
            minWidth: 50,
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <IconButton
                        color="secondary"
                        onClick={() => handleDownload(params.data)}>
                        <DownloadRounded/>
                    </IconButton>
                );
            }
        }
    ], []);

    const defaultColDef = useMemo(() => {
        return {
            lockPinned: true,
            resizable: true
        }
    }, []);

    /** Search Actions*/
    const handleResetForm = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        searchDocumentsForm.reset();
    }

    const handleDownload = (params: any) => {
        downloadDocument(params.file, params.documentName);
    }

    const buildReqParams = () => {
        const dataForm = searchDocumentsForm.getValues();
        const req = {
            ...dataForm,
            from: dataForm.from?.format("YYYY-MM-DD"),
            to: dataForm.to?.format("YYYY-MM-DD")
        }
        return getFormDirtyValues(searchDocumentsForm.formState.dirtyFields, req );
    }

    const handleSearch = () => {
        isSearching ? setIsSearching(false) : setIsSearching(true);
    }

    const serverSideDatasource: IServerSideDatasource = useMemo( () => ({
        getRows(params) {
            const reqParams = buildReqParams();
            const reqFullParams = {
                ...reqParams,
                functionality: props.functionality,
                id: props.id,
                paramRequest: {
                    pagination:  Number(params.request.endRow) - Number(params.request.startRow),
                    startIndex:  params.request.startRow,
                    endIndex:  params.request.endRow
                }};

            searchDocuments(reqFullParams)
                .then((response: any) => {
                    params.success({
                        rowData: response.historyDocuments.map((d: any) => ({
                                ...d,
                                uploadDate: d.uploadDate.substring(0,10)
                            })),
                        //rowData: response.historyDocuments,
                        rowCount: response.params.totCount,
                    })
                    setIsSearching(false);
                })
                .catch(() => {
                    params.fail();
                    setIsSearching(false);
                })
                .finally(() => {
                    setIsSearching(false);
                })
        }
    }), [isSearching]);

    return (
        <>
            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                    <FormProvider {...searchDocumentsForm}>
                        <Grid container columnSpacing={2}>
                            <Grid item xs={6} md={2} sx={{display: "flex", alignItems: "center"}}>
                                <FormDatePicker
                                    control={searchDocumentsForm.control}
                                    name="from"
                                    label={t('documentalDetails.form.fields.from')}
                                />
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <FormDatePicker
                                    control={searchDocumentsForm.control}
                                    name="to"
                                    label={t('documentalDetails.form.fields.to')}
                                />
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <FormAutocomplete
                                    name="documentName"
                                    control={searchDocumentsForm.control}
                                    label={t('documentalDetails.form.fields.documentName')}
                                    options={props.documentsTitles}
                                />
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <FormTextField
                                    name="fileName"
                                    control={searchDocumentsForm.control}
                                    label={t('documentalDetails.form.fields.fileName')}
                                />
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <FormTextField
                                    name="note"
                                    control={searchDocumentsForm.control}
                                    label={t('documentalDetails.form.fields.note')}
                                />
                            </Grid>
                            {/* Actions */}
                            <Grid item xs={6} md={2} sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                <Button variant={'text'}
                                        color={"accent"}
                                        startIcon={<ReplayRounded />}
                                        onClick={handleResetForm}>
                                    {t('documentalDetails.form.buttons.reset')}
                                </Button>
                                <Button color={"accent"}
                                        size={"icon"}
                                        onClick={searchDocumentsForm.handleSubmit(handleSearch)}>
                                    <SearchRounded />
                                </Button>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </Paper>
                <AgGridCustomTable
                    gridRef={gridRef}
                    defaultColDef={defaultColDef}
                    columnDefs={columnDefs}
                    rowModelType={'serverSide'}
                    serverSideInfiniteScroll={true}
                    serverSideDatasource={serverSideDatasource}
                    gridOptions={
                        {
                            onGridReady: (event) => event.api.sizeColumnsToFit(),
                        }
                    }
                />
                <CustomLoader loading={isFetching}/>
            </ContentBody>
        </>
    )
}

export default DocumentalHistory;